// From react-router rather than react-router-dom. In v7 the dom package is a
// re-export of this one, and it reaches its hooks through a subpath export
// that the Jest pinned by react-scripts 5 cannot resolve. Importing the
// source package keeps this component testable and changes nothing at
// runtime.
import { useLocation, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, BookOpen, Sparkles, FileCode, Users, Inbox } from "lucide-react";
import { cn } from "../lib/utils";
import { API } from "@/lib/api";
import { houdBij } from "@/lib/wachtendeVoorstellen";
import { useTaal } from "@/contexts/TaalContext";
import { useLidId } from "@/contexts/AuthContext";

/**
 * Bottom navigation for the pages that are not the conversation.
 *
 * Deliberately not on the chat screen. The composer already lives at the
 * bottom there, and a bar under it costs height and puts a row of navigation
 * exactly where the thumb has to reach to type. The sidebar drawer already
 * does that job on the chat screen. These pages have no composer and, before
 * this, no way out except one small back arrow in the top-left corner.
 *
 * Below the md breakpoint only. On a desktop the sidebar is always there.
 */
const DESTINATIONS = [
  { path: "/", label: "menu.chat", Icon: MessageSquare },
  { path: "/knowledge", label: "menu.knowledge", Icon: BookOpen },
  { path: "/skills", label: "menu.skills", Icon: Sparkles },
  { path: "/code", label: "menu.code", Icon: FileCode },
  // G4 (Frank, 18 okt 2026): de winkel staat in Instellingen; Teams blijft hier.
  { path: "/teams", label: "menu.teams", Icon: Users },
  // FIX 10, Franks glasronde van 11 oktober 2026: de postbus stond wel in de
  // zijbalk van de app en niet in deze balk van de PWA. Met dezelfde teller.
  { path: "/postbus", label: "menu.postbus", Icon: Inbox, teller: true },
];

/** Hoeveel er in de postbus wacht. Zelfde route en ritme als de zijbalk.
 *  F1c: per lid. Wisselt het lid, dan valt het oude getal weg en telt hij opnieuw. */
function usePostbusTeller() {
  const [aantal, setAantal] = useState(0);
  const lid = useLidId();
  useEffect(() => {
    setAantal(0);
    return houdBij({
      haal: async () => {
        const r = await axios.get(`${API}/postbus/teller`);
        return Number(r.data?.aantal) || 0;
      },
      zet: setAantal,
    });
  }, [lid]);
  return aantal;
}

export default function MobileBottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const postbus = usePostbusTeller();
  const { t } = useTaal();

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]"
      data-testid="bottom-nav"
    >
      <div className="flex">
        {DESTINATIONS.map(({ path, label: sleutel, Icon, teller }) => {
          const label = t(sleutel);
          const aantal = teller ? postbus : 0;
          // "/" would otherwise light up on every page.
          const active = path === "/" ? pathname === "/" : pathname.startsWith(path);
          return (
            <button
              key={path}
              type="button"
              onClick={() => navigate(path)}
              aria-current={active ? "page" : undefined}
              aria-label={aantal > 0 ? t("menu.postbusWachtend", { n: aantal }) : label}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[10px] font-medium transition-colors",
                active ? "text-[hsl(var(--accent))]" : "text-muted-foreground"
              )}
              // De id komt uit het PAD en niet uit het label: een vertaling
              // ("Store" werd "Winkel", 11 okt 2026) mag geen id veranderen.
              data-testid={`bottom-nav-${path === "/" ? "chat" : path.slice(1)}`}
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {aantal > 0 && (
                  <span
                    data-testid="bottom-nav-postbus-count"
                    className="absolute -right-2 -top-1 min-w-[16px] rounded-full bg-foreground px-1 text-[9px] leading-4 text-background"
                  >
                    {aantal}
                  </span>
                )}
              </span>
              {label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * How much room a page has to leave at the bottom so the bar covers nothing.
 * Exported so the pages cannot each guess a different number.
 */
export const BOTTOM_NAV_SPACER = "pb-[calc(56px+env(safe-area-inset-bottom))] md:pb-0";
